import { useQuery } from '@tanstack/react-query';
import { EImageVariant } from '@onesource/schemas';
import type { TMediaFileBase, TMediaFilePage, TMediaFileFilters, TUploadedFile } from '@onesource/schemas';
import { axiosServiceInstance } from '../axios';
import { getMediaUrl } from '../../utils';
import { MEDIA_ENTITY } from './entity';

export type TMediaFilesPage = {
  data: TUploadedFile[];
  total: number;
  page: number;
  limit: number;
};

function resolvePublicUrl(cdnUrls: TMediaFileBase['cdnUrls']): string | null {
  if (!cdnUrls) {
    return null;
  }

  const path =
    cdnUrls[EImageVariant.original] ??
    cdnUrls[EImageVariant.w2400] ??
    cdnUrls[EImageVariant.w1200] ??
    cdnUrls[EImageVariant.w800] ??
    Object.values(cdnUrls)[0] ??
    null;

  return getMediaUrl(path) || null;
}

function resolveCdnUrls(cdnUrls: TMediaFileBase['cdnUrls']): Partial<Record<EImageVariant, string>> | undefined {
  if (!cdnUrls) {
    return undefined;
  }
  return Object.fromEntries(Object.entries(cdnUrls).map(([variant, path]) => [variant, getMediaUrl(path)])) as Partial<
    Record<EImageVariant, string>
  >;
}

function mapToUploadedFile(raw: TMediaFileBase): TUploadedFile {
  return {
    key: raw.id,
    publicUrl: resolvePublicUrl(raw.cdnUrls),
    cdnUrls: resolveCdnUrls(raw.cdnUrls),
    filename: raw.originalName,
    size: raw.sizeBytes,
    lastModified: new Date(raw.createdAt),
    mimeType: raw.mimeType,
  };
}

type TMediaFilesQueryFilters = {
  page?: number;
  limit?: number;
  mimeType?: string;
  status?: TMediaFileFilters['status'];
  /** `true` = system only, `false` = personal only, `'all'` = all (admin only) */
  isSystem?: boolean | 'all';
};

type TUseGetMediaFilesQueryParams = TMediaFilesQueryFilters & {
  enabled?: boolean;
  refetchOnMount?: boolean | 'always';
};

export const getMediaFilesQueryKey = (filters?: TMediaFilesQueryFilters) => [MEDIA_ENTITY, 'list', filters];

export const getMediaFileQueryKey = (id: string) => [MEDIA_ENTITY, 'detail', id];

export const useGetMediaFileQuery = (id: string | undefined) => {
  return useQuery<TUploadedFile, Error>({
    queryKey: getMediaFileQueryKey(id ?? ''),
    queryFn: async () => {
      const res = await axiosServiceInstance.get<TMediaFileBase>(`/${MEDIA_ENTITY}/${id}`);
      return mapToUploadedFile(res.data);
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useGetMediaFilesQuery = (param: TUseGetMediaFilesQueryParams = {}) => {
  const { enabled = true, refetchOnMount, ...filters } = param;

  return useQuery<TMediaFilesPage, Error>({
    queryKey: getMediaFilesQueryKey(Object.keys(filters).length > 0 ? filters : undefined),
    queryFn: async () => {
      const res = await axiosServiceInstance.get<TMediaFilePage>(`/${MEDIA_ENTITY}`, {
        params: Object.keys(filters).length > 0 ? filters : undefined,
      });

      return {
        data: res.data.data.map(mapToUploadedFile),
        total: res.data.total,
        page: res.data.page,
        limit: res.data.limit,
      };
    },
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount,
  });
};
